import { PLAYER } from "../../../common/constants.ts";
import type { Conditions, Player } from "../../../common/types.ts";
import { idb } from "../../db/index.ts";
import { g, helpers, logEvent } from "../../util/index.ts";
import { planRoster, planRosterAddition, roleRating } from "../team/rosterComposition.baseball.ts";

export const MIN_DISSATISFACTION_AGE = 21;
const STAGES = [
	{ key: "waiting", min: 0, label: "Aguardando oportunidade", mood: 0 },
	{ key: "callUp", min: 20, label: "Quer promoção", mood: -1 },
	{ key: "trade", min: 50, label: "Quer troca", mood: -2.5 },
	{ key: "noResign", min: 90, label: "Menos disposto a renovar", mood: -5 },
] as const;
type StageKey = (typeof STAGES)[number]["key"];
export type MinorsWait = { frustration: number; season: number; gp: number; stage: StageKey };
type PlayerWithWait = Player & { minorsWait?: MinorsWait };
const stageFor = (n: number) => [...STAGES].reverse().find((s) => n >= s.min)!;
const bound = (n: number, low: number, high: number) => Math.max(low, Math.min(high, n));

// Compare current ability with MLB depth at his position, not potential or his blocked club's stars.
// One benchmark batch avoids a roster assignment for every minor leaguer each day.
export const majorLeagueBenchmarks = (players: Player[]) => {
	const byPos = new Map<string, number[]>();
	for (const p of players) {
		if (p.tid < 0 || g.get("season") - p.born.year < MIN_DISSATISFACTION_AGE) continue;
		const r = p.ratings.at(-1)!;
		const values = byPos.get(r.pos) ?? [];
		values.push(roleRating(p, r.pos));
		byPos.set(r.pos, values);
	}
	return new Map([...byPos].map(([pos, values]) => {
		values.sort((a, b) => a - b);
		return [pos, values[Math.floor((values.length - 1) / 4)]!];
	}));
};
export const minorLeagueReady = (p: Player, benchmarks: Map<string, number>) => {
	const r = p.ratings.at(-1)!;
	return roleRating(p, r.pos) >= (benchmarks.get(r.pos) ?? 50);
};
const patienceFactor = (p: Player) => {
	if (!g.get("playerMoodTraits")) return 1;
	return (p.moodTraits.includes("F") ? 1.4 : 1) * (p.moodTraits.includes("W") ? 1.2 : 1) * (p.moodTraits.includes("L") ? 0.6 : 1);
};
const gamesPlayed = (p: Player) => p.stats.filter((row) => row.season === g.get("season") && !row.playoffs)
	.reduce((n, row) => n + Math.max(row.gp ?? 0, (row as any).gpPit ?? 0), 0);

export const minorsStage = (p: Player) => {
	if (g.get("season") - p.born.year < MIN_DISSATISFACTION_AGE) return;
	const wait = (p as PlayerWithWait).minorsWait;
	if (!wait || wait.frustration < 1) return;
	const frustration = bound(wait.frustration, 0, 90);
	return { ...stageFor(frustration), frustration };
};

// A blocked, MLB-ready adult costs 30% less, rising to 50% with prolonged dissatisfaction.
// The owner's discount is used on both sides; negative-value contracts are handled by the caller.
export const minorLeagueTradeFactor = (p: Player, benchmarks: Map<string, number>) => {
	if (g.get("season") - p.born.year < MIN_DISSATISFACTION_AGE) return 1;
	const frustration = minorsStage(p)?.frustration ?? 0;
	if (p.tid === PLAYER.MINORS && p.injury.gamesRemaining === 0 && minorLeagueReady(p, benchmarks)) {
		return 0.7 - 0.2 * bound((frustration - 20) / 70, 0, 1);
	}
	// A roster label change alone cannot instantly erase an existing opportunity discount.
	return 1 - 0.3 * bound(frustration / 20, 0, 1) - 0.2 * bound((frustration - 20) / 70, 0, 1);
};

export const rememberMinorLeagueCallup = async (p: Player) => {
	if (p.tid !== PLAYER.MINORS || g.get("season") - p.born.year < MIN_DISSATISFACTION_AGE) return;
	const benchmarks = majorLeagueBenchmarks(await idb.cache.players.indexGetAll("playersByTid", [0, Infinity]));
	if (!minorLeagueReady(p, benchmarks)) return;
	const saved = p as PlayerWithWait;
	const frustration = Math.max(20, minorsStage(p)?.frustration ?? 0);
	saved.minorsWait = { frustration, season: g.get("season"), gp: gamesPlayed(p), stage: stageFor(frustration).key };
};

// Once per simulated regular-season day. Injuries and unfinished prospects do not build pressure.
export const updateMinorsSatisfaction = async (conditions: Conditions = {}) => {
	const season = g.get("season");
	const active = await idb.cache.players.indexGetAll("playersByTid", [0, Infinity]);
	const benchmarks = majorLeagueBenchmarks(active);
	for (const p of await idb.cache.players.indexGetAll("playersByTid", PLAYER.MINORS) as PlayerWithWait[]) {
		if (p.minorTid === undefined) continue;
		if (season - p.born.year < MIN_DISSATISFACTION_AGE) {
			if (p.minorsWait) { delete p.minorsWait; await idb.cache.players.put(p); }
			continue;
		}
		const old = minorsStage(p);
		let frustration = old?.frustration ?? 0;
		if (p.injury.gamesRemaining === 0) frustration = minorLeagueReady(p, benchmarks)
			? Math.min(90, frustration + patienceFactor(p)) : Math.max(0, frustration - 0.5);
		if (frustration === (old?.frustration ?? 0)) continue;
		if (frustration === 0) delete p.minorsWait;
		else {
			const stage = stageFor(frustration);
			p.minorsWait = { frustration, season, gp: gamesPlayed(p), stage: stage.key };
			if (stage.min > (old?.min ?? 0) && g.get("userTids").includes(p.minorTid)) await logEvent({
				type: "info", text: `<a href="${helpers.leagueUrl(["player", p.pid])}">${p.firstName} ${p.lastName}</a> (Minors): ${stage.label}. Está pronto para uma oportunidade na MLB.`,
				pids: [p.pid], tids: [p.minorTid], persistent: true,
			}, conditions);
		}
		await idb.cache.players.put(p);
	}
	for (const p of active as PlayerWithWait[]) {
		if (!p.minorsWait || p.injury.gamesRemaining > 0) continue;
		if (season - p.born.year < MIN_DISSATISFACTION_AGE) delete p.minorsWait;
		else {
			const gp = gamesPlayed(p);
			const lastGp = p.minorsWait.season === season ? p.minorsWait.gp : gp;
			const frustration = Math.max(0, Math.min(90, p.minorsWait.frustration) - 1 - 2 * Math.max(0, gp - lastGp));
			if (frustration === 0) delete p.minorsWait;
			else p.minorsWait = { frustration, season, gp, stage: stageFor(frustration).key };
		}
		await idb.cache.players.put(p);
	}
};

const ROLE_LABELS: Record<string, string> = { SP: "a rotation spot", RP: "a bullpen spot", DH: "the DH spot", UT: "a utility spot", ANY: "a depth spot" };
export const bigClubRole = (p: Player, roster: Player[], baseline = planRoster(roster, g.get("maxRosterSize"), g.get("dh") !== "none")) => {
	const plan = planRosterAddition(roster, p, baseline);
	const i = plan.assignment.findIndex((index) => index === roster.length);
	const slot = i >= 0 ? plan.slots[i] : undefined;
	if (!slot || slot.weight < 0.2) return;
	const starter = slot.weight >= 0.6;
	return { pos: slot.pos, starter, label: ROLE_LABELS[slot.pos] ?? `${starter ? "the starting" : "the backup"} ${slot.pos} job` };
};
